import {
  BarChart3,
  ClipboardCheck,
  LayoutDashboard,
  LifeBuoy,
  Map,
  ScrollText,
  Settings,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';

interface Item {
  to: string;
  labelKey: string;
  Icon: LucideIcon;
}

const NAV: readonly Item[] = [
  { to: '/admin', labelKey: 'admin.dashboardLink', Icon: LayoutDashboard },
  { to: '/admin/insights', labelKey: 'admin.insightsLink', Icon: BarChart3 },
  { to: '/admin/users', labelKey: 'admin.usersLink', Icon: Users },
  { to: '/admin/map', labelKey: 'admin.mapLink', Icon: Map },
  { to: '/admin/verifications', labelKey: 'admin.verificationsLink', Icon: ClipboardCheck },
  { to: '/admin/tickets', labelKey: 'admin.ticketsLink', Icon: LifeBuoy },
  { to: '/admin/audit', labelKey: 'admin.auditLink', Icon: ScrollText },
  { to: '/admin/settings', labelKey: 'admin.settingsLink', Icon: Settings },
];

/**
 * Desktop-first admin shell (per design handoff §Admin journey): a 220px
 * gradient sidebar on wide viewports collapses to a scrollable pill row on
 * mobile so admin still works on-device. The outer AppShell's bottom-tab bar
 * stays for cross-app nav (admin ↔ profile ↔ mode-switcher).
 */
export function AdminShell(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="admin-shell">
      <aside className="admin-shell__sidebar" aria-label={t('admin.title')}>
        <div className="admin-shell__brand">
          <span className="admin-shell__mark" aria-hidden>
            ب
          </span>
          <span className="admin-shell__brandName">{t('admin.title')}</span>
        </div>
        <nav className="admin-shell__nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin'}
              className={({ isActive }) =>
                'admin-shell__navItem' + (isActive ? ' admin-shell__navItem--active' : '')
              }
            >
              <item.Icon size={18} aria-hidden />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="admin-shell__body">
        <Outlet />
      </div>
    </div>
  );
}
